import { useState, useCallback } from 'react';
import { Product, SearchFiltersType } from '../pages/ProductMatrixNew';

interface UseProductSearchReturn {
  products: Product[];
  loading: boolean;
  error: string | null;
  searchProducts: (filters: SearchFiltersType) => Promise<void>;
}

export const useProductSearch = (): UseProductSearchReturn => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const searchProducts = useCallback(async (filters: SearchFiltersType) => {
    setLoading(true);
    setError(null);

    try {
      // Build query parameters
      const params = new URLSearchParams();
      
      Object.entries(filters).forEach(([key, value]) => {
        if (value && typeof value === 'string' && value.trim()) {
          params.append(key, value.trim());
        }
      });

      const url = `/api/products${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        // Region groupings for countries (matching server-side logic)
        const REGION_GROUPS = {
          'North America': ['US', 'Canada'],
          'Europe': ['UK', 'Ireland', 'Germany', 'Spain', 'Austria', 'Belgium', 'Bulgaria', 'Croatia', 
                    'Cyprus', 'Czechia', 'Denmark', 'Estonia', 'Finland', 'France', 'Greece', 
                    'Hungary', 'Iceland', 'Italy', 'Latvia', 'Liechtenstein', 'Lithuania', 
                    'Luxembourg', 'Malta', 'Netherlands', 'Norway', 'Poland', 'Portugal', 
                    'Romania', 'Slovakia', 'Slovenia', 'Sweden', 'Switzerland'],
          'APAC': ['Australia', 'New Zealand'],
          'Middle East': ['Israel'],
          'Latin America': ['Brazil']
        };

        // Helper function to get regions for a product based on available countries
        const getAvailableRegions = (countries: Record<string, boolean>) => {
          const regions: string[] = [];
          Object.entries(REGION_GROUPS).forEach(([region, regionCountries]) => {
            const hasCountryInRegion = regionCountries.some(country => countries[country] === true);
            if (hasCountryInRegion) {
              regions.push(region);
            }
          });
          return regions;
        };

        // Parse countries JSON string to object and calculate regions for each product
        const processedProducts = (data.data || []).map((product: any) => {
          const countries = typeof product.countries === 'string' 
            ? JSON.parse(product.countries) 
            : product.countries || {};
          
          return {
            ...product,
            countries,
            regions: getAvailableRegions(countries) // Calculate regions from countries
          };
        });
        setProducts(processedProducts);
      } else {
        throw new Error(data.error || 'Failed to fetch products');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred';
      setError(errorMessage);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    products,
    loading,
    error,
    searchProducts
  };
};